<template>
  <div class="ob-col ob-col--wide">
    <div class="ob-head">
      <span class="ob-head__icon" style="background: rgba(16, 185, 129, 0.12); color: #10b981"><span class="mdi mdi-view-grid-outline"></span></span>
      <h2 class="ob-head__title">Registra las naves de tu parque</h2>
      <p class="ob-head__sub">Cada nave puede tener su propio contrato e inquilino</p>
    </div>

    <div class="plots">
      <div class="plots__form">
        <div class="plots__cols">
          <span>Código</span><span>Zona</span><span>Superficie</span><span></span>
        </div>
        <transition-group name="row" tag="div">
          <div v-for="(row, i) in ob.plots" :key="row.key" class="prow" :class="{ 'prow--saved': row.saved }">
            <input v-model="row.name" class="ob-input ob-input--mono" placeholder="A-01" maxlength="40" :disabled="row.saved" :aria-label="`Código de la nave ${i + 1}`" />
            <div class="prow__zone">
              <select v-model="row.zone" class="ob-select" :disabled="row.saved" :aria-label="`Zona de la nave ${i + 1}`">
                <option value="">Sin zona</option>
                <option v-for="z in ZONE_PRESETS" :key="z" :value="z">{{ z }}</option>
                <option :value="CUSTOM_ZONE">Otra…</option>
              </select>
              <input v-if="row.zone === CUSTOM_ZONE" v-model="row.customZone" class="ob-input" placeholder="Nombre de la zona" maxlength="40" :disabled="row.saved" />
            </div>
            <div class="ob-input-wrap">
              <input v-model="row.areaM2" class="ob-input" inputmode="decimal" placeholder="0" :disabled="row.saved" :aria-label="`Metros cuadrados de la nave ${i + 1}`" @input="row.areaM2 = String(row.areaM2).replace(/[^0-9.]/g, '')" />
              <span class="ob-suffix">m²</span>
            </div>
            <span v-if="row.saved" class="prow__saved mdi mdi-check-circle" title="Guardada"></span>
            <button v-else type="button" class="prow__remove" :disabled="ob.plots.length === 1" aria-label="Quitar esta nave" @click="remove(row)">
              <span class="mdi mdi-close"></span>
            </button>
          </div>
        </transition-group>

        <div class="plots__actions">
          <button type="button" class="ob-link" @click="addRow">＋ Agregar otra nave</button>
          <button type="button" class="ob-link ob-link--gray" @click="csvInput.click()">
            <span class="mdi mdi-upload"></span> Importar desde CSV
          </button>
          <input ref="csvInput" type="file" accept=".csv,text/csv" hidden @change="onCsv" />
        </div>
        <p class="ob-note">El archivo CSV puede traer las columnas: código, zona y m².</p>
        <p v-if="csvMessage" class="ob-note" style="color: #b45309">{{ csvMessage }}</p>
        <p v-if="ob.error" class="ob-error">{{ ob.error }}</p>
      </div>

      <aside class="plots__grid" aria-label="Naves registradas">
        <div class="plots__grid-title">Tu parque <b>{{ cards.length }}</b></div>
        <div v-if="!cards.length" class="plots__empty">Aquí irá tomando forma tu parque</div>
        <div v-else class="cards">
          <transition-group name="pop" tag="div" class="cards__inner">
            <div v-for="c in cards" :key="c.key" class="card" :style="{ background: zoneColor(c.zoneName) }" :title="c.zoneName || 'Sin zona'">
              {{ c.name }}
            </div>
          </transition-group>
        </div>
      </aside>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { ob, newPlotRow } from '@/services/onboardingStore'
import { plotZone } from './onboardingFlow'
import { ZONE_PRESETS, CUSTOM_ZONE, zoneColor } from './onboardingFormat'

const csvInput = ref(null)
const csvMessage = ref('')

const cards = computed(() => ob.plots.filter((r) => r.name.trim()).map((r) => ({ key: r.key, name: r.name.trim(), zoneName: plotZone(r) })))

function addRow() {
  ob.plots.push(newPlotRow())
}

function remove(row) {
  if (ob.plots.length > 1) ob.plots = ob.plots.filter((r) => r.key !== row.key)
}

// Acepta encabezados en español o inglés. Sin encabezados, entiende las columnas por posición:
// nombre, zona y m² (si solo hay dos columnas y la segunda es un número, es el área).
function parseCsv(text) {
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter((l) => l.trim())
  if (!lines.length) return []
  const split = (l) => l.split(l.includes(';') && !l.includes(',') ? ';' : ',').map((c) => c.trim().replace(/^"|"$/g, ''))
  const headers = split(lines[0]).map((h) => h.toLowerCase())
  const idx = (names) => headers.findIndex((h) => names.includes(h))
  const nameIdx = idx(['codigo', 'código', 'nombre', 'name', 'nave'])
  const zoneIdx = idx(['zona', 'zone'])
  const areaIdx = idx(['m2', 'm²', 'area', 'área', 'area_m2', 'superficie'])
  const hasHeader = nameIdx >= 0 || zoneIdx >= 0 || areaIdx >= 0
  const isNumber = (v) => /^[0-9]+([.,][0-9]+)?$/.test(String(v || '').trim())
  return (hasHeader ? lines.slice(1) : lines).map((line) => {
    const cols = split(line)
    if (hasHeader) {
      return {
        name: cols[nameIdx >= 0 ? nameIdx : 0] || '',
        zone: zoneIdx >= 0 ? cols[zoneIdx] || '' : '',
        area: areaIdx >= 0 ? cols[areaIdx] || '' : '',
      }
    }
    if (cols.length >= 3) return { name: cols[0] || '', zone: cols[1] || '', area: cols[2] || '' }
    if (cols.length === 2) return isNumber(cols[1]) ? { name: cols[0] || '', zone: '', area: cols[1] } : { name: cols[0] || '', zone: cols[1] || '', area: '' }
    return { name: cols[0] || '', zone: '', area: '' }
  }).filter((r) => r.name)
}

function onCsv(e) {
  const file = e.target.files?.[0]
  e.target.value = ''
  csvMessage.value = ''
  if (!file) return
  const reader = new FileReader()
  reader.onload = () => {
    const rows = parseCsv(String(reader.result || ''))
    if (!rows.length) return (csvMessage.value = 'No se encontraron naves en el archivo.')
    const existing = new Set(ob.plots.map((r) => r.name.trim().toLowerCase()).filter(Boolean))
    const room = Math.max(0, 500 - ob.plots.length)
    const fresh = rows.filter((r) => !existing.has(r.name.toLowerCase())).slice(0, room)
    // La fila vacía inicial se reemplaza por lo importado.
    const kept = ob.plots.filter((r) => r.saved || r.name.trim())
    const added = fresh.map((r) => {
      const row = newPlotRow()
      row.name = r.name.slice(0, 40)
      row.areaM2 = String(r.area).replace(/[^0-9.]/g, '')
      const preset = ZONE_PRESETS.find((z) => z.toLowerCase() === r.zone.toLowerCase())
      if (preset) row.zone = preset
      else if (r.zone) { row.zone = CUSTOM_ZONE; row.customZone = r.zone.slice(0, 40) }
      return row
    })
    ob.plots = [...kept, ...added, ...(added.length || kept.length ? [] : [newPlotRow()])]
    const skipped = rows.length - fresh.length
    csvMessage.value = skipped > 0 ? `${added.length} naves agregadas. ${skipped} se omitieron por estar repetidas.` : ''
  }
  reader.readAsText(file)
}
</script>

<style scoped>
.plots { display: grid; grid-template-columns: minmax(0, 560px) minmax(0, 280px); gap: 36px; justify-content: center; align-items: start; }
.plots__cols { display: grid; grid-template-columns: 1fr 1.2fr 1fr 28px; gap: 10px; margin-bottom: 6px; font: 500 11px var(--font-body); color: #94a3b8; text-transform: uppercase; letter-spacing: 0.04em; }
.prow { display: grid; grid-template-columns: 1fr 1.2fr 1fr 28px; gap: 10px; align-items: start; margin-bottom: 10px; }
.prow__zone { display: flex; flex-direction: column; gap: 6px; }
.prow__remove { height: 42px; width: 28px; border: none; background: none; color: #94a3b8; cursor: pointer; font-size: 16px; }
.prow__remove:hover:not(:disabled) { color: #e84545; }
.prow__remove:disabled { opacity: 0.3; cursor: default; }
.prow__saved { height: 42px; display: flex; align-items: center; color: #10b981; font-size: 18px; }
.plots__actions { display: flex; gap: 18px; align-items: center; margin-top: 6px; }

.plots__grid { position: sticky; top: 8px; border: 1px solid #f1f5f9; border-radius: 10px; padding: 14px; background: #fafbfd; min-height: 120px; }
.plots__grid-title { font: 500 11px var(--font-body); color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 10px; }
.plots__grid-title b { color: #1e3a5f; margin-left: 4px; }
.plots__empty { font: 400 12px var(--font-body); color: #b6c0cd; text-align: center; padding: 24px 0; }
.cards { max-height: 340px; overflow: auto; }
.cards__inner { display: flex; flex-wrap: wrap; gap: 8px; }
.card { width: 64px; height: 40px; border-radius: 6px; border: 1px solid rgba(30, 58, 95, 0.08); display: flex; align-items: center; justify-content: center; font: 500 11px var(--font-body); color: #1e3a5f; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding: 0 4px; }

.row-enter-active { transition: opacity 0.2s, transform 0.2s; }
.row-enter-from { opacity: 0; transform: translateY(-8px); }
.pop-enter-active { transition: transform 0.2s ease-out, opacity 0.2s; }
.pop-enter-from { transform: scale(0); opacity: 0; }

@media (max-width: 960px) {
  .plots { grid-template-columns: minmax(0, 560px); }
  .plots__grid { position: static; }
}
@media (prefers-reduced-motion: reduce) {
  .row-enter-active, .pop-enter-active { transition: none; }
}
</style>